// Traditional Nepali (Devanagari) keyboard layout mapping.
// Maps physical KeyboardEvent.code -> {normal, shift} Devanagari characters.

export const NEPALI_MAP = {
    Backquote: { normal: "ञ", shift: "ञ्" },
    Digit1: { normal: "१", shift: "ज्ञ" },
    Digit2: { normal: "२", shift: "द्द" },
    Digit3: { normal: "३", shift: "घ्" },
    Digit4: { normal: "४", shift: "द्ध" },
    Digit5: { normal: "५", shift: "छ्" },
    Digit6: { normal: "६", shift: "ट्ट" },
    Digit7: { normal: "७", shift: "ठ्ठ" },
    Digit8: { normal: "८", shift: "ड्ढ" },
    Digit9: { normal: "९", shift: "ध्" },
    Digit0: { normal: "०", shift: "न्न" },
    Minus: { normal: "(", shift: ")" },
    Equal: { normal: "ं", shift: "ः" },

    KeyQ: { normal: "त्र", shift: "त्त" },
    KeyW: { normal: "ध", shift: "ध्" },
    KeyE: { normal: "भ", shift: "भ्" },
    KeyR: { normal: "च", shift: "च्" },
    KeyT: { normal: "त", shift: "त्" },
    KeyY: { normal: "थ", shift: "थ्" },
    KeyU: { normal: "ग", shift: "ग्" },
    KeyI: { normal: "ष", shift: "क्ष्" },
    KeyO: { normal: "इ", shift: "ई" },
    KeyP: { normal: "ए", shift: "ऐ" },
    BracketLeft: { normal: "ृ", shift: "र्" },
    BracketRight: { normal: "े", shift: "ै" },
    Backslash: { normal: "्", shift: "ँ" },

    KeyA: { normal: "ब", shift: "०" },
    KeyS: { normal: "क", shift: "क्" },
    KeyD: { normal: "म", shift: "म्" },
    KeyF: { normal: "ा", shift: "ँ" },
    KeyG: { normal: "न", shift: "न्" },
    KeyH: { normal: "ज", shift: "ज्" },
    KeyJ: { normal: "व", shift: "व्" },
    KeyK: { normal: "प", shift: "प्" },
    KeyL: { normal: "ि", shift: "ी" },
    Semicolon: { normal: "स", shift: "स्" },
    Quote: { normal: "ु", shift: "ू" },

    KeyZ: { normal: "श", shift: "श्" },
    KeyX: { normal: "ह", shift: "ह्" },
    KeyC: { normal: "अ", shift: "आ" },
    KeyV: { normal: "ख", shift: "ख्" },
    KeyB: { normal: "द", shift: "द्" },
    KeyN: { normal: "ल", shift: "ल्" },
    KeyM: { normal: "फ", shift: "फ्" },
    Comma: { normal: ",", shift: "<" },
    Period: { normal: "।", shift: ">" },
    Slash: { normal: "र", shift: "्र" },
    Space: { normal: " ", shift: " " },
};

// Reverse lookup: Devanagari char -> physical key code (for highlighting the next key)
export const CHAR_TO_KEY = (() => {
    const map = {};
    for (const [code, { normal, shift }] of Object.entries(NEPALI_MAP)) {
        if (normal && !(normal in map)) map[normal] = { code, shift: false };
        if (shift && !(shift in map)) map[shift] = { code, shift: true };
    }
    return map;
})();

// Keyboard layout for rendering the on-screen keyboard
export const KEYBOARD_ROWS = [
    [
        { code: "Backquote", en: "`", enShift: "~" },
        { code: "Digit1", en: "1", enShift: "!" },
        { code: "Digit2", en: "2", enShift: "@" },
        { code: "Digit3", en: "3", enShift: "#" },
        { code: "Digit4", en: "4", enShift: "$" },
        { code: "Digit5", en: "5", enShift: "%" },
        { code: "Digit6", en: "6", enShift: "^" },
        { code: "Digit7", en: "7", enShift: "&" },
        { code: "Digit8", en: "8", enShift: "*" },
        { code: "Digit9", en: "9", enShift: "(" },
        { code: "Digit0", en: "0", enShift: ")" },
        { code: "Minus", en: "-", enShift: "_" },
        { code: "Equal", en: "=", enShift: "+" },
        { code: "Backspace", en: "⌫", wide: 5 },
    ],
    [
        { code: "Tab", en: "Tab", wide: 4 },
        { code: "KeyQ", en: "Q" },
        { code: "KeyW", en: "W" },
        { code: "KeyE", en: "E" },
        { code: "KeyR", en: "R" },
        { code: "KeyT", en: "T" },
        { code: "KeyY", en: "Y" },
        { code: "KeyU", en: "U" },
        { code: "KeyI", en: "I" },
        { code: "KeyO", en: "O" },
        { code: "KeyP", en: "P" },
        { code: "BracketLeft", en: "[", enShift: "{" },
        { code: "BracketRight", en: "]", enShift: "}" },
        { code: "Backslash", en: "\\", enShift: "|", wide: 3 },
    ],
    [
        { code: "CapsLock", en: "Caps", wide: 5 },
        { code: "KeyA", en: "A" },
        { code: "KeyS", en: "S" },
        { code: "KeyD", en: "D" },
        { code: "KeyF", en: "F" },
        { code: "KeyG", en: "G" },
        { code: "KeyH", en: "H" },
        { code: "KeyJ", en: "J" },
        { code: "KeyK", en: "K" },
        { code: "KeyL", en: "L" },
        { code: "Semicolon", en: ";", enShift: ":" },
        { code: "Quote", en: "'", enShift: '"' },
        { code: "Enter", en: "Enter", wide: 6 },
    ],
    [
        { code: "ShiftLeft", en: "Shift", wide: 7 },
        { code: "KeyZ", en: "Z" },
        { code: "KeyX", en: "X" },
        { code: "KeyC", en: "C" },
        { code: "KeyV", en: "V" },
        { code: "KeyB", en: "B" },
        { code: "KeyN", en: "N" },
        { code: "KeyM", en: "M" },
        { code: "Comma", en: ",", enShift: "<" },
        { code: "Period", en: ".", enShift: ">" },
        { code: "Slash", en: "/", enShift: "?" },
        { code: "ShiftRight", en: "Shift", wide: 8 },
    ],
    [{ code: "Space", en: "Space", wide: 40 }],
];
